/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { useGetScrollToTopButtonStyles } from "./index.styles";

const isWindowTarget = (el) =>
  !el || el === document.documentElement || el === document.scrollingElement;

// This app renders pages inside an inner wrapper with its own overflow-y:
// auto (not the window), so walk up from a stable anchor node to find the
// element that's actually scrolling.
const getScrollParent = (node) => {
  let el = node?.parentElement;
  while (el && el !== document.body) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

/**
 * Floating "back to top" button.
 * Pass `containerRef` to track/scroll a known scrollable element (e.g. a
 * modal's content pane); omit it to auto-detect the page's scroll container.
 */
const ScrollToTopButton = ({ containerRef, variant = "fixed", threshold = 200 }) => {
  const classes = useGetScrollToTopButtonStyles(variant);
  const anchorRef = useRef(null);
  const scrollTargetRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = containerRef?.current || getScrollParent(anchorRef.current);
    scrollTargetRef.current = target;
    const listenTarget = isWindowTarget(target) ? window : target;

    const handleScroll = () => {
      const scrollTop = isWindowTarget(target) ? window.scrollY : target.scrollTop;
      setVisible(scrollTop > threshold);
    };

    handleScroll();
    listenTarget.addEventListener("scroll", handleScroll);
    return () => listenTarget.removeEventListener("scroll", handleScroll);
  }, [containerRef, threshold]);

  const handleClick = () => {
    const target = scrollTargetRef.current;
    if (isWindowTarget(target)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      target.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <span ref={anchorRef} style={{ display: "none" }} />
      {visible && (
        <button
          type="button"
          aria-label="Scroll to top"
          onClick={handleClick}
          css={classes.button}
        >
          <KeyboardArrowUpIcon fontSize="small" />
        </button>
      )}
    </>
  );
};

export default ScrollToTopButton;
